import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  TextField,
  Button,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

import {
  Orders,
  fetchOrders,
  fetchOrdersStatus,
  updateOrderStatus,
} from "../../app/services/OrdersService";

import TableSkeleton from "../loader/TableSkeleton";
import PageWrapper from "../../app/shared/common/PageWrapper";
import DropDown from "../../app/shared/common/DropDown";
import { fetchOrganisation } from "../../app/services/OrganisationService";
import { fetchOutletByOrganisation } from "../../app/services/OutletService";

const OrderManagement = () => {
  const navigate = useNavigate();

  interface Status {
    id: number;
    name: string;
  }

  const [orders, setOrders] = useState<Orders[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [statusList, setStatusList] = useState<Status[]>([]);
  const [organisations, setOrganisations] = useState<{ id: number; name: string }[]>([]);
  const [organisationId, setOrganisationId] = useState<string>("");
  const [outlets, setOutlets] = useState<{ id: number; name: string }[]>([]);
  const [outletId, setOutletId] = useState<string>("");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [orderId, setOrderId] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<number | "">("");

  const columns: GridColDef[] = [
    {
      field: "created_at",
      headerName: "Date",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const dateStr = params.value;
        const formattedDate = new Date(dateStr).toISOString().split("T")[0];
        return <span>{formattedDate}</span>;
      },
    },
    {
      field: "unique_id",
      headerName: "Order Id",
      flex: 2,
      renderCell: (params) => {
        const id = params.value;
        return (
          <span
            style={{ color: "#1976d2", cursor: "pointer", textDecoration: "underline" }}
            onClick={() => navigate(`/orders/${id}`)}
          >
            {id}
          </span>
        );
      },
    },
    {
      field: "organisation",
      headerName: "Organisation",
      flex: 1,
      renderCell: (params) => (
        <span>{params.row.org?.name ?? "No Data"}</span>
      ),
    },
    {
      field: "outlet",
      headerName: "Outlet",
      flex: 1,
      renderCell: (params) => (
        <span>{params.row.outlet?.name ?? "No Data"}</span>
      ),
    },
   {
      field: "item",
      headerName: "Item",
      flex: 1,
      renderCell: (params) => (
        <span>{params.row.items?.length ?? "No Data"}</span>
      ),
    },
    {
      field: "qty",
      headerName: "Ordered Qty",
      flex: 1,
      renderCell: (params) => {
        const totalQty = params.row.items?.reduce(
          (sum: number, item: any) => sum + (item.quantity || 0),
          0
        );
        return <span>{totalQty ?? "No Data"}</span>;
      },
    },
  
    {
      field: "action",
      headerName: "Action",
      flex: 2,
      renderCell: (params) => {
        const orderId = params.row.unique_id;
        const currentStatusId = params.row.statusHistory?.[0]?.status?.id || "";
        const currentStatusName = params.row.statusHistory?.[0]?.status?.name || "";

        const isActionDisabled = ["cancelled", "delivered", "returned"].includes(
          currentStatusName.toLowerCase()
        );

        const handleStatusChange = async (event: any) => {
          const newStatusId = event.target.value;

          try {
            const res = await updateOrderStatus(orderId, newStatusId);

            if (res.status === "success") {
              toast.success("Status updated successfully");
              setOrders((prev) =>
                prev.map((order) =>
                  order.unique_id === orderId
                    ? {
                        ...order,
                        statusHistory: [
                          {
                            status: statusList.find(
                              (s) => s.id === newStatusId
                            ) || {
                              id: newStatusId,
                              name: "Unknown",
                            },
                          },
                        ],
                      }
                    : order
                )
              );
            } else {
              toast.error("Failed to update status");
            }
          } catch (err) {
            toast.error("Error while updating status");
          
          }
        };

        return (
          <Box>
            <FormControl fullWidth size="medium">
              <Select
                value={currentStatusId}
                onChange={handleStatusChange}
                disabled={isActionDisabled}
              >
                {statusList.map((status) => (
                  <MenuItem key={status.id} value={status.id}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                        }}
                      />
                      {status.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );
      },
    },
  ];


  const loadOrders = async () => {
    try {
      setLoading(true);
      const res = await fetchOrders({
        startDate,
        endDate,
        statusId: selectedStatus === "" ? null : selectedStatus,
        orderId,
        orgId: organisationId ? Number(organisationId) : null,
        outletId: outletId ? Number(outletId) : null,
      });

      if (res?.status === "success") {
        const sortedData = res.data.sort(
          (a: Orders, b: Orders) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        setOrders(sortedData);
      } else {
        toast.error(res?.message || "Unknown error");
      }
    } catch (err) {
      toast.error("Failed to load orders.");
    } finally {
      setLoading(false);
    }
  };

  const loadStatuses = async () => {
      try {
        const res = await fetchOrdersStatus();
        if (res.status === "success") {
          setStatusList(res.data);
        }
      } catch (error) {
        console.error("Failed to fetch status list", error);
      }
    };

  const loadOrganisations = async () => {
    try {
      const res = await fetchOrganisation();
      if (res.status === "success") {
        setOrganisations(res.data || []);
      }
    } catch (err) {
      toast.error("Failed to load organisations");
    }
  };

  const loadOutlets = async (orgId: number) => {
    try {
      const res = await fetchOutletByOrganisation(orgId);
      if (res?.status === "success") {
        setOutlets(res.data || []);
      } else {
        setOutlets([]);
      }
    } catch (err) {
      toast.error("Failed to load outlets");
      setOutlets([]);
    }
  };

  const exportToExcel = () => {
    if (orders.length === 0) {
      toast.warning("No data to export");
      return;
    }

    const exportData = orders.flatMap((order) =>
      (order.items || []).map((item: any) => ({
        Date: new Date(order.created_at).toISOString().split("T")[0],
        "Order Id": order.unique_id,
        Organisation: order.org?.name || "No Data",
        Outlet: order.outlet?.name || "No Data",
        Status: order.statusHistory?.[0]?.status?.name || "No Data",
        "Product Name": item.product?.name || "No Data",
        SKU: item.product?.sku || "No Data",
        Quantity: item.quantity || 0,
        "Updated Quantity": item.updated_quantity || 0,
        Price: item.price || "0.00",
        Total: item.total || 0,
        "Tax Percent": item.tax_percent || "0.00",
        "Tax Value": item.tax_value || 0,
        "Total with Tax": item.total_with_tax || 0,
      }))
    );

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");

    // Set column widths
    const columnWidths = [
      { wch: 12 },
      { wch: 15 },
      { wch: 20 },
      { wch: 20 },
      { wch: 15 },
      { wch: 25 },
      { wch: 15 },
      { wch: 10 },
      { wch: 15 },
      { wch: 10 },
      { wch: 10 },
      { wch: 12 },
      { wch: 10 },
      { wch: 15 },
    ];
    worksheet["!cols"] = columnWidths;

    const fileName = `Orders_${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    toast.success("Data exported successfully");
  };

  useEffect(() => {
    loadStatuses();
    loadOrganisations();
  }, []);

  useEffect(() => {
    loadOrders();
  }, [startDate, endDate, orderId, selectedStatus, organisationId, outletId]);

  useEffect(() => {
    if (organisationId) {
      loadOutlets(Number(organisationId));
      // Reset outlet selection when organisation changes
      setOutletId("");
    } else {
      // Clear outlets when no organisation is selected
      setOutlets([]);
      setOutletId("");
    }
  }, [organisationId]);

  return (
    <PageWrapper title={"Orders"}>

      <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Order ID"
          value={orderId}
          onChange={(e) => setOrderId(e.target.value)}
          sx={{ minWidth: 150 }}
        />
        <FormControl sx={{ minWidth: 120 }}>
          <Select<number | "">
            displayEmpty
            value={selectedStatus}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedStatus(value === "" ? "" : Number(value));
            }}
          >
            <MenuItem value="">All Status</MenuItem>
            {statusList.map((status) => (
              <MenuItem key={status.id} value={status.id}>
                {status.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <DropDown
          label="Filter by organisation"
          value={organisationId}
          onChange={setOrganisationId}
          options={organisations}
          allLabel="All Organisation"
          minWidth={150}
        />
        <DropDown
          label="Filter by outlet"
          value={outletId}
          onChange={setOutletId}
          options={outlets}
          allLabel="All Outlets"
          minWidth={150}
          disabled={!organisationId}
        />

        <Button
          variant="contained"
          color="success"
          onClick={exportToExcel}
          sx={{ backgroundColor: "#4caf50" }}
        >
          Export to Excel
        </Button>
      </Box>

      <Box sx={{ width: "100%" }}>
        {loading ? (
          <TableSkeleton rows={8} columns={8} />
        ) : orders.length > 0 ? (
          <DataGrid
            autoHeight
            rows={orders}
            columns={columns}
            getRowId={(row) => row.id}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 20, page: 0 },
              },
            }}
            pageSizeOptions={[20, 50, 100]}
            checkboxSelection={false}
            disableRowSelectionOnClick
            sx={{
              backgroundColor: "#fff",
              borderRadius: 2,
              p: 2,
            }}
          />
        ) : (
          <Typography>No data found</Typography>
        )}
      </Box>
    </PageWrapper>
  );
};

export default OrderManagement;
